import React, { useCallback, type FC, useMemo } from 'react';
import type { ContentProps } from '@atlaskit/popup';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { OnCloseProps, CustomTriggerComponentType } from '../../types.tsx';
import { projectActionMenuEntryPoint } from '../entrypoint.tsx';
import { MenuContainer } from '../menu-container/index.tsx';
import { Skeleton } from '../skeleton/index.tsx';

export type EntryPointContentProps = OnCloseProps & {
	projectKey: string;
	projectName?: string;
	CustomTrigger?: CustomTriggerComponentType;
	onClick?: () => void;
	isCorePremiumUserSeat?: boolean;
	renderAdditionalMenuItems?: (params?: ContentProps) => React.ReactNode;
	shouldRenderToParent?: boolean;
	themePicker?: React.ReactNode;
};
export const EntryPointContent: FC<EntryPointContentProps> = ({
	CustomTrigger,
	onClick,
	renderAdditionalMenuItems,
	isCorePremiumUserSeat = false,
	projectKey,
	shouldRenderToParent,
	themePicker,
	projectName,
}: EntryPointContentProps) => {
	const cloudId = useCloudId();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		projectActionMenuEntryPoint,
		useMemo(
			() => ({
				cloudId,
				projectKey,
			}),
			[cloudId, projectKey],
		),
	);
	const buttonTriggerRef = useEntryPointButtonTrigger(entryPointActions);

	const content = useCallback(
		(contentProps: ContentProps) => (
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="project-actions-menu-popup-content"
				packageName="jira-navigation-apps-project-action-menu"
				runtimeProps={{
					isCorePremiumUserSeat,
					renderAdditionalMenuItems,
					contentProps,
					themePicker,
				}}
				fallback={<Skeleton />}
			/>
		),
		[entryPointReferenceSubject, renderAdditionalMenuItems, isCorePremiumUserSeat, themePicker],
	);

	return (
		<MenuContainer
			{...(fg('jsm_aiiy_for_nav_item_sidebar') ? { projectName } : {})}
			content={content}
			CustomTrigger={CustomTrigger}
			onClick={onClick}
			buttonTriggerRef={buttonTriggerRef}
			shouldRenderToParent={shouldRenderToParent}
		/>
	);
};
